/**
 * The word for a key, when the browser will not say one.
 *
 * `KeyboardEvent.code` names physical keys in hardware terms, and `ui/keyLabels.ts` asks the
 * layout API what each one prints where that API exists. This file is the fallback and the
 * specials: the codes that never print anything, spelled the way a control legend would say them.
 *
 * Words rather than glyphs, because a legend is read aloud between two players and an arrow glyph
 * has no pronunciation.
 */

/** What a row says for an action with no key on it. A visible absence, never an empty cell. */
export const KEY_UNBOUND = 'Unbound';

const NAMED_CODES: Readonly<Record<string, string>> = {
  Space: 'Space',
  ShiftLeft: 'Left Shift',
  ShiftRight: 'Right Shift',
  ControlLeft: 'Left Ctrl',
  ControlRight: 'Right Ctrl',
  AltLeft: 'Left Alt',
  AltRight: 'Right Alt',
  Tab: 'Tab',
  Enter: 'Enter',
  Backspace: 'Backspace',
  CapsLock: 'Caps Lock',
  ArrowUp: 'Up Arrow',
  ArrowDown: 'Down Arrow',
  ArrowLeft: 'Left Arrow',
  ArrowRight: 'Right Arrow',
  Home: 'Home',
  End: 'End',
  PageUp: 'Page Up',
  PageDown: 'Page Down',
  Insert: 'Insert',
  Delete: 'Delete',
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
};

/** The legend word for a code, without asking the layout: `KeyR` is R, `Digit5` is 5. */
export function keyWord(code: string): string {
  const named = NAMED_CODES[code];
  if (named !== undefined) return named;
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  if (code.startsWith('Numpad')) return `Numpad ${code.slice(6)}`;
  return code;
}
